import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { formatCompactMoney } from '@/lib/money';
import { cn } from '@/lib/utils';
import type { FinanceChannelFieldAvailability } from '@/api/generated/model';
import {
  financeReadyState,
  NotReadyBadge,
  NotReadyNote,
} from './FinanceNotReady';

export type PaymentChannelView = {
  provider: string;
  label: string;
  gross: number;
  grossAvailability: FinanceChannelFieldAvailability;
  providerFee: number | null;
  providerFeeAvailability: FinanceChannelFieldAvailability;
  refundsChargebacks?: number | null;
  refundsChargebacksAvailability?: FinanceChannelFieldAvailability;
  netSettlement: number | null;
  netSettlementAvailability: FinanceChannelFieldAvailability;
  note?: string;
};

function channelFieldReason(
  label: string,
  availability: FinanceChannelFieldAvailability,
): string {
  const state = financeReadyState(availability);
  if (label === 'Provider Fee') {
    return state === 'partial'
      ? 'Partial — provider fee columns are empty for this window. Gross is still live.'
      : 'Apple IAP commission is not stored on payments yet.';
  }
  if (label === 'Refunds & Chargebacks') {
    return 'Not ready — refund and chargeback event tables unlock in Phase 4.';
  }
  if (label === 'Net Settlement') {
    return state === 'partial'
      ? 'Partial — Net Settlement waits on provider fees. Do not invent Gross − Creator.'
      : 'Net Settlement waits on Apple IAP commission, which is not stored yet.';
  }
  return state === 'partial'
    ? 'Partial — some figures in this channel are incomplete.'
    : 'Not ready — this figure is not wired yet.';
}

function MoneyRow({
  label,
  amount,
  currency,
  availability,
}: {
  label: string;
  amount: number | null;
  currency: string;
  availability: FinanceChannelFieldAvailability;
}) {
  const state = financeReadyState(availability);
  const live = state === 'live' && amount != null;
  return (
    <div className="flex items-center justify-between gap-2 py-1.5">
      <span className="text-[11px] text-muted-foreground">{label}</span>
      <span className="flex items-center gap-1.5">
        <NotReadyBadge state={state} reason={channelFieldReason(label, availability)} />
        <span className={cn('text-sm font-semibold tabular-nums', !live && 'text-muted-foreground')}>
          {live ? formatCompactMoney(amount, currency) : '—'}
        </span>
      </span>
    </div>
  );
}

/** Right column: stacked channel cards (Apple IAP + Stripe only). */
export function PaymentChannelSummaryCard({
  channels,
  currency,
}: {
  channels: PaymentChannelView[];
  currency: string;
}) {
  const anyIncomplete = channels.some(
    (channel) =>
      financeReadyState(channel.providerFeeAvailability) !== 'live' ||
      financeReadyState(channel.netSettlementAvailability) !== 'live' ||
      financeReadyState(channel.refundsChargebacksAvailability ?? 'COMING_SOON') !== 'live',
  );

  return (
    <Card className="overflow-hidden bg-card/80 shadow-none">
      <CardHeader className="space-y-1 p-4 pb-2">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="text-sm font-medium text-sky-400">Payment Channel Summary</CardTitle>
          {anyIncomplete ? (
            <NotReadyBadge
              state="partial"
              reason="Gross is live. Provider Fee and Net Settlement need fee columns; Refunds & Chargebacks await Phase 4."
            />
          ) : null}
        </div>
        <p className="text-[11px] text-muted-foreground">
          Apple IAP + Stripe. Creator Incentives is an expense module, not a channel.
        </p>
      </CardHeader>
      <CardContent className="space-y-3 p-4 pt-1">
        {channels.map((channel) => (
          <div
            key={channel.provider}
            className="rounded-md border border-border/60 bg-background/40 px-3 py-2.5"
          >
            <p className="mb-1 text-sm font-medium">{channel.label}</p>
            <div className="divide-y divide-border/40">
              <MoneyRow
                label="Gross"
                amount={channel.gross}
                currency={currency}
                availability={channel.grossAvailability}
              />
              <MoneyRow
                label="Provider Fee"
                amount={channel.providerFee}
                currency={currency}
                availability={channel.providerFeeAvailability}
              />
              <MoneyRow
                label="Refunds & Chargebacks"
                amount={channel.refundsChargebacks ?? null}
                currency={currency}
                availability={channel.refundsChargebacksAvailability ?? 'COMING_SOON'}
              />
              <MoneyRow
                label="Net Settlement"
                amount={channel.netSettlement}
                currency={currency}
                availability={channel.netSettlementAvailability}
              />
            </div>
            {channel.note ? (
              <p className="mt-2 text-[10px] leading-4 text-muted-foreground">{channel.note}</p>
            ) : null}
          </div>
        ))}
        {anyIncomplete ? (
          <NotReadyNote>
            Gross is live. Provider Fee / Net Settlement need fee columns; Refunds & Chargebacks
            await Phase 4.
          </NotReadyNote>
        ) : null}
      </CardContent>
    </Card>
  );
}
